/**
 * frecency — localStorage-backed usage overlay.
 *
 * Records the {count, lastUsedAt} per result id. Adds a small bonus to
 * the raw match score, scaled so that even a hot frecent item never
 * outranks a much stronger match — only breaks ties.
 *
 * Storage key: hh-palette-frecency-v1. Capped at 500 entries; oldest
 * (by lastUsedAt) get pruned. Cleared on logout via clearFrecency().
 */

const KEY = "hh-palette-frecency-v1";
const MAX_ENTRIES = 500;
const MAX_BONUS = 35;
const DAY_MS = 24 * 60 * 60 * 1000;

interface FrecencyEntry {
  count: number;
  lastUsedAt: number;
}

type FrecencyMap = Record<string, FrecencyEntry>;

let cache: FrecencyMap | null = null;

function safeRead(): FrecencyMap {
  if (cache) return cache;
  if (typeof window === "undefined") return (cache = {});
  try {
    const raw = window.localStorage.getItem(KEY);
    cache = raw ? (JSON.parse(raw) as FrecencyMap) : {};
  } catch {
    cache = {};
  }
  return cache;
}

function safeWrite(map: FrecencyMap): void {
  cache = map;
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    // quota / privacy mode — silently drop
  }
}

function prune(map: FrecencyMap): FrecencyMap {
  const ids = Object.keys(map);
  if (ids.length <= MAX_ENTRIES) return map;
  const sorted = ids.sort((a, b) => map[a].lastUsedAt - map[b].lastUsedAt);
  const drop = sorted.slice(0, ids.length - MAX_ENTRIES);
  for (const id of drop) delete map[id];
  return map;
}

/** Record a selection. Call from the row's onSelect handler. */
export function recordSelection(id: string): void {
  const map = { ...safeRead() };
  const now = Date.now();
  const prev = map[id];
  map[id] = {
    count: (prev?.count ?? 0) + 1,
    lastUsedAt: now,
  };
  safeWrite(prune(map));
}

/** Return the bonus this id should add to its match score. */
export function frecencyBonus(id: string, now: number = Date.now()): number {
  const entry = safeRead()[id];
  if (!entry) return 0;
  const daysAgo = (now - entry.lastUsedAt) / DAY_MS;
  // log to dampen frequency dominance; decay curves toward 0 over ~14 days
  const usage = Math.log2(1 + entry.count);
  const recency = 1 / (1 + daysAgo / 7);
  return Math.min(MAX_BONUS, usage * recency * 12);
}

export function clearFrecency(): void {
  cache = {};
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
}
